// Feedback React Query Hooks
// Implements data fetching, caching, and mutations for feedback system

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { feedbackService } from '../services/feedbackService';
import { splitConversationsByBucket } from '../utils/feedbackBuckets';
import {
  countUnreadByConversation,
  getReadCursor,
  loadReadState,
  markConversationRead,
  subscribeToReadState
} from '../utils/feedbackReadState';
import type {
  FeedbackListParams,
  ConversationWithMessages,
  CreateFeedbackRequest,
  AddMessageRequest,
  UpdateConversationRequest
} from '../types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const feedbackKeys = {
  all: ['feedback'] as const,
  conversations: () => [...feedbackKeys.all, 'conversations'] as const,
  conversationsList: (params: FeedbackListParams) =>
    [...feedbackKeys.conversations(), 'list', params] as const,
  conversation: (id: string) => [...feedbackKeys.conversations(), 'detail', id] as const,
  categories: () => [...feedbackKeys.all, 'categories'] as const,
  stats: () => [...feedbackKeys.all, 'stats'] as const,
  users: () => [...feedbackKeys.all, 'users'] as const,
  activity: (since: string) => [...feedbackKeys.all, 'activity', since] as const
};

// ============================================================================
// CONVERSATION QUERIES
// ============================================================================

/**
 * Hook to fetch all feedback conversations with pagination and filtering
 */
export const useFeedbackConversations = (
  params: FeedbackListParams = {},
  options: {
    enabled?: boolean;
    refetchInterval?: number;
    refetchIntervalInBackground?: boolean;
  } = {}
) => {
  return useQuery({
    queryKey: feedbackKeys.conversationsList(params),
    queryFn: () => feedbackService.getConversations(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: options.refetchInterval || 30000, // 30 seconds default
    refetchIntervalInBackground: options.refetchIntervalInBackground || false,
    enabled: options.enabled !== false,
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401, 403) or other 4xx errors
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        if (
          errorMsg.includes('401') ||
          errorMsg.includes('403') ||
          errorMsg.includes('unauthorized') ||
          errorMsg.includes('forbidden') ||
          errorMsg.includes('4')
        ) {
          return false;
        }
      }
      return failureCount < 2; // Reduce retries for better UX
    },
    // Suppress console errors for auth issues
    meta: {
      suppressErrors: true
    }
  });
};

/**
 * Hook to fetch a single conversation with all messages
 */
export const useFeedbackConversation = (
  conversationId: string | undefined,
  userId?: string,
  options: {
    enabled?: boolean;
    refetchInterval?: number;
    refetchIntervalInBackground?: boolean;
  } = {}
) => {
  return useQuery({
    queryKey: feedbackKeys.conversation(conversationId || ''),
    queryFn: () => {
      if (!conversationId) {
        return null;
      }
      return feedbackService.getConversationById(conversationId, userId);
    },
    staleTime: 1000 * 5, // 5 seconds - much faster for selected conversations
    refetchInterval: options.refetchInterval || 30000, // 30 seconds default
    refetchIntervalInBackground: options.refetchIntervalInBackground || false,
    enabled: options.enabled !== false && !!conversationId,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (like 406 Not Acceptable)
      if (
        error instanceof Error &&
        (error.message.includes('4') || error.message.includes('PGRST116'))
      ) {
        return false;
      }
      return failureCount < 3;
    }
  });
};

/**
 * Hook to fetch feedback categories
 */
export const useFeedbackCategories = () => {
  return useQuery({
    queryKey: feedbackKeys.categories(),
    queryFn: () => feedbackService.getCategories(),
    staleTime: 1000 * 60 * 10, // 10 minutes - categories don't change often
    retry: 3
  });
};

/**
 * Hook to fetch feedback statistics (admin only)
 */
export const useFeedbackStats = (
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {}
) => {
  return useQuery({
    queryKey: feedbackKeys.stats(),
    queryFn: () => feedbackService.getFeedbackStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: options.refetchInterval || 60000, // 1 minute default
    enabled: options.enabled !== false,
    retry: 3
  });
};

/**
 * Hook to fetch users for assignment (admin only)
 */
export const useFeedbackUsers = () => {
  return useQuery({
    queryKey: feedbackKeys.users(),
    queryFn: () => feedbackService.getUsers(),
    staleTime: 1000 * 60 * 10, // 10 minutes - users don't change often
    retry: 3
  });
};

// ============================================================================
// CONVERSATION MUTATIONS
// ============================================================================

/**
 * Hook to create a new feedback conversation
 */
export const useCreateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFeedbackRequest) => feedbackService.createConversation(request),
    onSuccess: data => {
      // Invalidate conversations list to show new conversation
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversations()
      });

      // Also refetch conversations immediately to ensure UI updates
      queryClient.refetchQueries({
        queryKey: feedbackKeys.conversations()
      });

      // Prefetch the new conversation
      queryClient.setQueryData(feedbackKeys.conversation(data.conversation.id), data);

      toast.success(
        'Feedback submitted successfully! Our product team has been notified and you can ' +
          'expect to hear back within 24-48 hours.'
      );
    },
    onError: error => {
      console.error('Error creating feedback:', error);
      toast.error(`Failed to submit feedback: ${error.message}`);
    }
  });
};

/**
 * Hook to add a message to a conversation
 */
export const useAddMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddMessageRequest) => feedbackService.addMessage(request),
    onSuccess: (message, variables) => {
      // Invalidate the specific conversation to refetch with new message
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversation(variables.conversation_id)
      });

      // Also invalidate conversations list to update message count
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversations()
      });

      toast.success('Message sent!');
    },
    onError: error => {
      console.error('Error adding message:', error);
      toast.error(`Failed to send message: ${error.message}`);
    }
  });
};

/**
 * Hook to update conversation status, priority, or assignment (admin only)
 */
export const useUpdateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      updates
    }: {
      conversationId: string;
      updates: UpdateConversationRequest;
    }) => feedbackService.updateConversation(conversationId, updates),
    onSuccess: (data, variables) => {
      // Update the conversation in cache
      queryClient.setQueryData(
        feedbackKeys.conversation(variables.conversationId),
        (oldData: ConversationWithMessages | undefined) => {
          if (oldData) {
            return { ...oldData, ...data };
          }
          return oldData;
        }
      );

      // Invalidate conversations list to update status display
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversations()
      });

      // Invalidate stats to update counts
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.stats()
      });

      toast.success('Conversation updated successfully!');
    },
    onError: error => {
      console.error('Error updating conversation:', error);
      toast.error(`Failed to update conversation: ${error.message}`);
    }
  });
};

// ============================================================================
// UPVOTE MUTATIONS
// ============================================================================

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to mark messages as read
 */
export const useMarkMessagesAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      messageIds
    }: {
      conversationId: string;
      messageIds?: string[];
    }) => feedbackService.markMessagesAsRead(conversationId, messageIds),
    onSuccess: (_, variables) => {
      // Invalidate the conversation to update read status
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversation(variables.conversationId)
      });
    },
    onError: error => {
      console.error('Error marking messages as read:', error);
      // Don't show toast for read errors as they're not critical
    }
  });
};

// ============================================================================
// COMBINED HOOKS FOR COMMON USE CASES
// ============================================================================

/* eslint-disable max-lines-per-function */
/**
 * Hook for feedback dialog - fetches all conversations (same as main),
 * splits for Active/Feature requests/Resolved tabs client-side
 */
export const useFeedbackDialog = (
  selectedConversationId?: string,
  userId?: string,
  options: {
    refetchInterval?: number;
    enabled?: boolean;
  } = {}
) => {
  const queryClient = useQueryClient();
  const enabled = options.enabled !== false;

  // Single query with no filters - matches main branch behavior, RLS handles tenant
  // Use large page_size to ensure all conversations are fetched for client-side splitting
  const conversationsQuery = useFeedbackConversations(
    { sort_by: 'updated_at', sort_order: 'desc', page_size: 500 },
    { refetchInterval: options.refetchInterval, enabled }
  );

  const selectedConversationQuery = useFeedbackConversation(selectedConversationId, userId, {
    enabled: !!selectedConversationId && enabled,
    refetchInterval: options.refetchInterval
  });

  // Split client-side: Active = open/in_progress (minus confirmed feature requests);
  // Feature requests = confirmed feature requests; Resolved = resolved from last year
  const { activeConversations, featureRequestConversations, resolvedConversations } =
    useMemo(() => {
      const { active, featureRequests, resolved } = splitConversationsByBucket(
        conversationsQuery.data?.conversations ?? []
      );
      return {
        activeConversations: active,
        featureRequestConversations: featureRequests,
        resolvedConversations: resolved
      };
    }, [conversationsQuery.data?.conversations]);

  // Prefetch conversation data when hovering over conversation items
  const prefetchConversation = (conversationId: string) => {
    queryClient.prefetchQuery({
      queryKey: feedbackKeys.conversation(conversationId),
      queryFn: () => feedbackService.getConversationById(conversationId, userId),
      staleTime: 1000 * 5 // 5 seconds
    });
  };

  return {
    activeConversations,
    featureRequestConversations,
    resolvedConversations,
    selectedConversation: selectedConversationQuery.data,
    conversationsLoading: conversationsQuery.isLoading,
    selectedLoading: selectedConversationQuery.isLoading,
    conversationsError: conversationsQuery.error,
    selectedError: selectedConversationQuery.error,
    refetchConversations: conversationsQuery.refetch,
    refetchSelected: selectedConversationQuery.refetch,
    prefetchConversation
  };
};
/* eslint-enable max-lines-per-function */

// ============================================================================
// UNREAD REPLIES
// ============================================================================

/** Only look back this far for unread replies */
const UNREAD_LOOKBACK_DAYS = 90;

/** Re-render whenever this user's read state changes (this tab or another) */
const useReadStateVersion = () => {
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeToReadState(() => setVersion(v => v + 1)), []);
  return version;
};

/**
 * Unread reply counts per conversation for the current user
 */
export const useFeedbackUnread = (
  userId?: string,
  options: { refetchInterval?: number; enabled?: boolean } = {}
) => {
  const version = useReadStateVersion();
  const enabled = options.enabled !== false && !!userId;

  // Fetch from the later of the user's baseline and the lookback window
  const since = useMemo(() => {
    if (!userId) return '';
    const lookback = new Date(Date.now() - UNREAD_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
    const baseline = new Date(loadReadState(userId).baseline);
    return (baseline > lookback ? baseline : lookback).toISOString();
  }, [userId]);

  const activityQuery = useQuery({
    queryKey: feedbackKeys.activity(since),
    queryFn: () => feedbackService.getMessageActivity(since),
    enabled,
    ...(options.refetchInterval && { refetchInterval: options.refetchInterval }),
    staleTime: 1000 * 5
  });

  // Fresh snapshot whenever read state changes (version bumps)
  const readState = useMemo(
    () => (userId ? { ...loadReadState(userId), version } : undefined),
    [userId, version]
  );

  const unreadCounts = useMemo((): Record<string, number> => {
    if (!userId || !readState || !activityQuery.data) return {};
    return countUnreadByConversation(activityQuery.data, userId, readState);
  }, [activityQuery.data, userId, readState]);

  return { unreadCounts };
};

/**
 * Marks the open conversation as read and remembers where the "New" divider goes.
 * Returns the cursor captured when the conversation was opened (stable while it stays open).
 */
export const useConversationReadTracking = (
  conversation: ConversationWithMessages | null | undefined,
  userId?: string
) => {
  const [opened, setOpened] = useState<{ id: string; since: string }>();
  const conversationId = conversation?.id;
  const messages = conversation?.messages;
  const latestAt = messages?.[messages.length - 1]?.created_at;

  useEffect(() => {
    if (!conversationId || !userId) return;
    if (opened?.id !== conversationId) {
      setOpened({
        id: conversationId,
        since: getReadCursor(loadReadState(userId), conversationId)
      });
    }
    if (latestAt) {
      markConversationRead(userId, conversationId, latestAt);
    }
  }, [conversationId, latestAt, userId, opened?.id]);

  const isOpenedHere = !!opened && opened.id === conversationId;
  return { newMessagesSince: isOpenedHere ? opened.since : undefined };
};

/**
 * Hook for admin dashboard - combines stats, conversations, and users
 */
export const useFeedbackAdmin = (
  filters: FeedbackListParams['filters'] = {},
  options: {
    refetchInterval?: number;
  } = {}
) => {
  const statsQuery = useFeedbackStats({
    refetchInterval: options.refetchInterval || 60000
  });

  const conversationsQuery = useFeedbackConversations(
    {
      filters,
      sort_by: 'created_at',
      sort_order: 'desc',
      // Load everything (PostgREST max) so older feedback never drops off the admin list
      page_size: 1000
    },
    { refetchInterval: options.refetchInterval || 30000 }
  );

  const usersQuery = useFeedbackUsers();

  return {
    stats: statsQuery.data,
    conversations: conversationsQuery.data?.conversations || [],
    users: usersQuery.data || [],
    statsLoading: statsQuery.isLoading,
    conversationsLoading: conversationsQuery.isLoading,
    usersLoading: usersQuery.isLoading,
    statsError: statsQuery.error,
    conversationsError: conversationsQuery.error,
    usersError: usersQuery.error,
    refetchStats: statsQuery.refetch,
    refetchConversations: conversationsQuery.refetch,
    refetchUsers: usersQuery.refetch
  };
};

/**
 * Hook for closing/resolving feedback
 */
export const useCloseFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      await feedbackService.closeFeedback(conversationId);
    },
    onSuccess: (_, conversationId) => {
      // Invalidate and refetch conversations
      queryClient.invalidateQueries({ queryKey: feedbackKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.conversation(conversationId) });
      toast.success('Feedback marked as resolved');
    },
    onError: (error: Error) => {
      toast.error(`Failed to close feedback: ${error.message}`);
    }
  });
};

/**
 * Hook for updating a message's text content (admin only)
 */
export const useUpdateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, message }: { messageId: string; message: string }) =>
      feedbackService.updateMessage(messageId, message),
    onSuccess: data => {
      // Invalidate the conversation to refetch with updated message
      if (data.conversation_id) {
        queryClient.invalidateQueries({
          queryKey: feedbackKeys.conversation(data.conversation_id)
        });
      }
      // Also invalidate conversations list
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversations()
      });
      toast.success('Message updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update message: ${error.message}`);
    }
  });
};

/**
 * Hook for deleting a message (admin or message owner)
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId }: { messageId: string; conversationId: string }) =>
      feedbackService.deleteMessage(messageId),
    onSuccess: (_, variables) => {
      // Invalidate the conversation to refetch without the deleted message
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversation(variables.conversationId)
      });
      // Also invalidate conversations list to update message count
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.conversations()
      });
      toast.success('Message deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete message: ${error.message}`);
    }
  });
};

/**
 * Hook for deleting feedback
 */
export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      await feedbackService.deleteFeedback(conversationId);
    },
    onSuccess: (_, conversationId) => {
      // Remove the conversation from cache and invalidate queries
      queryClient.removeQueries({ queryKey: feedbackKeys.conversation(conversationId) });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.conversations() });
      toast.success('Feedback deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete feedback: ${error.message}`);
    }
  });
};
